/**
 * inlineMarkdown — the minimal inline-Markdown renderer used by every
 * public surface that stores prose in a JSON string column.
 *
 * WHY IT EXISTS (2026-10-17)
 * --------------------------
 * `ArticleDetail.tsx` grew a hand-rolled `inlineRender()` for `body_md`, the
 * one column that stores free-form Markdown. Two other surfaces store prose
 * in JSON and were reading it as plain text:
 *
 *   - `cms_lectures.body_md_<locale>` — one Markdown string, rendered by
 *     `LectureDetailPage.tsx` (2026-10-29: it used to be
 *     `body_sections_<locale>` — `[{h2, p}]`).
 *   - `cms_product_pages.blocks_<locale>`   — `[{type, …}]`, rendered by the
 *     product-page renderer.
 *
 * `server/lib/content-prompt.ts` tells the model to emit `![alt](url)` for
 * inline media, and the ingest path happily writes it into any of those `p` /
 * `content` fields. Those pages then showed the literal Markdown source to
 * visitors. The first row to hit this was
 * `hktdc-electronics-fair-20261013` (id=17): both photos printed twice, once
 * as a real `<figure>` and once as `![講者手持咪…](/uploads/2026-10/….webp)`.
 *
 * So this module is deliberately NOT a general Markdown parser. It handles
 * exactly the inline subset that can appear in these columns:
 *
 *   `![alt](src)`  image    `[text](href)`  link
 *   `**bold**`     strong   `*em*`           em      `` `code` ``  code
 *
 * A bare URL (`https://example.com`) is linkified too — see the last
 * alternative of `INLINE_RE` for why.
 *
 * SECURITY
 * --------
 * This returns React elements built from the captured groups, never a
 * `dangerouslySetInnerHTML` string, so there is no HTML parsing step: the
 * `href`/`src` land in JSX attributes and the rest becomes text nodes. That
 * is strictly stronger than the `src/admin/lib/render-markdown.ts` preview
 * path, which escapes-then-re-introduces via regex.
 *
 * React does NOT scheme-check an `href` — a `javascript:` URL in an anchor
 * is a live XSS vector, and React only ever warned about it. So every href
 * that reaches an anchor goes through `linkHref()` first: `http:`/`https:`
 * only, plus site-relative paths. Anything else degrades to plain text.
 *
 * The one behaviour worth stating out loud: an unrecognised or unsafe
 * construct is left as literal text, so no content silently disappears. An
 * image's `src` is passed through unchanged — a `javascript:` src cannot
 * execute script in an `<img>`, and the media library's own `/uploads/…`
 * paths are relative, so a scheme check would break them.
 */
import React, { type ReactNode } from 'react'
import { safeExternalUrl } from './safeExternalUrl'
import { cmsMediaIndex } from '@/content/cms/media.generated'
import { cmsMediaSrcSet } from './cmsMediaUrl'
import { uuidFromUploadPath } from './mediaPath'
import { scanShortcodes } from '@/components/embed/shortcodes'
import ShortcodeEmbed from '@/components/embed/ShortcodeEmbed'

/**
 * Matches, in priority order: image, link, bold, em, code, bare URL.
 *
 * The bare-URL alternative is LAST on purpose. The regex engine takes the
 * FIRST alternative that matches at a given position, so a URL that is
 * already the href of a `[text](url)` link is consumed by the link
 * alternative and never seen again — the two can never both fire on the
 * same URL.
 *
 * Captured groups, in order:
 *   1,2 → image alt / src
 *   3,4 → link text / href
 *   5   → bold
 *   6   → em
 *   7   → code
 *   9   → bare URL
 *
 * The URL character class excludes `(`, `)`, `[` and `]` so a URL written
 * the way an operator naturally writes it — `沈偉銓(https://example.com/)` —
 * stops at the closing parenthesis and leaves it as ordinary text.
 */
const INLINE_RE =
  /!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|(^|[^*])\*([^*]+)\*|`([^`]+)`|(https?:\/\/[^\s<>"'()\[\]]+)/g

/**
 * Punctuation that may sit AFTER a URL in running prose and is not part of
 * it. `See https://example.com.` links the URL, not the full stop.
 */
const URL_TRAILING_PUNCTUATION = /[.,;:!?、。，；：！？]+$/

/**
 * The href an anchor may carry, or undefined when the value must not
 * become a link.
 *
 *   - `http:` / `https:` — allowed (checked by `safeExternalUrl`).
 *   - `/path` — allowed: a site-relative path (the media library's
 *     `/uploads/…` images).
 *   - `//host/path` — REFUSED. A protocol-relative URL inherits the page's
 *     scheme, so on an `http:` page it silently downgrades to plaintext.
 *   - `javascript:`, `data:`, `vbscript:`, anything else — REFUSED.
 */
function linkHref(href: string): string | undefined {
  const trimmed = href.trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith('//')) return undefined
  if (trimmed.startsWith('/')) return trimmed
  return safeExternalUrl(trimmed)
}

/** Class for an inline image. Matches the article body so both surfaces
 *  look identical once the same photo appears in either place. */
const IMG_CLASS = 'rounded-yada-md border border-border shadow-yada my-6 w-full h-auto'

export interface InlineMarkdownProps {
  /** The raw string. May contain newlines — callers split paragraphs first. */
  text: string
  /** Override the inline-image class if the container has its own spacing. */
  imageClassName?: string
}

/**
 * The layout width of an inline image, as a `sizes` value.
 *
 * WHY THE CALLER HAS TO STATE IT
 * ------------------------------
 * A `srcset` candidate is only safe when the browser knows how wide the
 * image will be RENDERED: `sizes` says that, and the browser then picks the
 * smallest candidate that covers it. Get it wrong in the small direction and
 * the browser upscales a too-small file — visibly worse than no `srcset` at
 * all.
 *
 * So `sizes` is NOT defaulted here. The caller states the column width when
 * the image is width-constrained (`w-full`), and passes nothing when it is
 * not:
 *
 *   - width-constrained (`w-full`) → the image is exactly as wide as its
 *     column, so the column's width IS the layout width. Pass it.
 *   - NOT width-constrained → the image renders at its INTRINSIC width, which
 *     is the original file's pixel width. The ladder's largest rung is by
 *     construction NARROWER than that, so any candidate the browser picked
 *     would be stretched — a `srcset` there would blur the image. Pass
 *     nothing, and the image keeps its original file.
 *
 * With no `sizes` (and therefore no `srcset`), the image still gets its
 * `width`/`height` attributes below, which is what removes the layout shift.
 */
export interface InlineMarkdownOptions {
  imageClassName?: string
  /** The `sizes` value for inline images — pass it ONLY for a width-constrained image. */
  imageSizes?: string
}

/**
 * Render the inline subset of Markdown inside `text`.
 *
 * Returns a fragment. Keys are derived from the node index so React can
 * re-order on re-render without remounting the whole subtree.
 */
export function renderInlineMarkdown(
  text: string,
  options: InlineMarkdownOptions = {},
): ReactNode[] {
  const imgClass = options.imageClassName ?? IMG_CLASS
  const out: ReactNode[] = []
  let last = 0
  let m: RegExpExecArray | null

  INLINE_RE.lastIndex = 0
  while ((m = INLINE_RE.exec(text)) !== null) {
    // A zero-length match would spin forever. Every alternative consumes at
    // least 2 chars, so this is unreachable in practice — the guard is there
    // because a future edit to the pattern could make it reachable, and a
    // hang at render time is far worse than a dropped node.
    if (m[0].length === 0) {
      INLINE_RE.lastIndex += 1
      continue
    }

    if (m.index > last) out.push(text.slice(last, m.index))
    const key = out.length

    if (m[1] !== undefined && m[2] !== undefined) {
      // Image. `alt` is echoed verbatim so an alt-less image still produces
      // a non-empty accessible name if the URL is meaningful; empty alt is a
      // valid authoring choice and is passed through as empty.
      //
      // The `src` is passed through UNCHANGED (see the header) — the media
      // library's relative `/uploads/…` paths must keep working.
      const src = m[2]
      // The media row behind this path, when the path is a media-library
      // upload. A static asset (`/media/…`, `/products/…`) yields null and
      // the image is rendered exactly as before.
      const uuid = uuidFromUploadPath(src)
      const entry = uuid ? cmsMediaIndex[uuid] : undefined
      // The NATIVE size, so the browser reserves the box before the image
      // decodes. Without these two attributes every body image pushed the
      // text below it down the page as it loaded.
      const width = entry?.width ?? undefined
      const height = entry?.height ?? undefined
      // A `srcset` only when the caller stated the layout width — see
      // InlineMarkdownOptions. Without one, the image keeps its original
      // file rather than risk an upscale.
      const srcSet = entry && options.imageSizes ? cmsMediaSrcSet(uuid) : undefined
      out.push(
        <img
          key={key}
          src={src}
          srcSet={srcSet}
          sizes={srcSet ? options.imageSizes : undefined}
          alt={m[1]}
          loading="lazy"
          decoding="async"
          width={width}
          height={height}
          className={imgClass}
        />,
      )
    } else if (m[3] !== undefined && m[4] !== undefined) {
      // A link whose href is not a safe URL is shown as plain text rather
      // than as an anchor — an unsafe scheme in an href is a live XSS
      // vector, and React does not filter it.
      const href = linkHref(m[4])
      if (href) {
        out.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:text-primary-dark"
          >
            {m[3]}
          </a>,
        )
      } else {
        out.push(m[3])
      }
    } else if (m[5] !== undefined) {
      out.push(<strong key={key}>{m[5]}</strong>)
    } else if (m[6] !== undefined && m[7] !== undefined) {
      // Group 6 is the `(^|[^*])` leading character. It must be emitted before
      // the <em> or the text it swallowed disappears.
      out.push(m[6])
      out.push(<em key={key}>{m[7]}</em>)
    } else if (m[8] !== undefined) {
      out.push(
        <code key={key} className="rounded-yada-sm bg-surface-2 px-1 py-0.5 text-sm font-mono">
          {m[8]}
        </code>,
      )
    } else if (m[9] !== undefined) {
      // A bare URL. This is the form an operator actually types — the CMS's
      // rich-text editor stores what they type, and TinyMCE's autolink does
      // not fire for a URL wrapped in brackets, so `沈偉銓(https://aif.hk/)`
      // reaches the body as plain text. Linkifying it here is what makes
      // that read as a link on the page instead of as a wall of URL.
      const raw = m[9]
      const url = raw.replace(URL_TRAILING_PUNCTUATION, '')
      const trailing = raw.slice(url.length)
      const href = linkHref(url)
      if (href) {
        out.push(
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:text-primary-dark"
          >
            {url}
          </a>,
        )
        // The full stop (or comma) that ended the sentence is text, not part
        // of the link.
        if (trailing) out.push(trailing)
      } else {
        out.push(raw)
      }
    }

    last = INLINE_RE.lastIndex
  }

  if (last < text.length) out.push(text.slice(last))
  return out
}

/**
 * Render inline Markdown with shortcode expansion.
 *
 * Like `renderInlineMarkdown`, but additionally scans `text` for `{{key}}`
 * shortcode tokens and renders each as `<ShortcodeEmbed shortkey={key} />`.
 * Plain-text segments flow through `renderInlineMarkdown` as before. Every
 * node gets a unique key so concatenating segments inside one block is safe.
 */
export function renderInlineMarkdownWithShortcodes(
  text: string,
  options: InlineMarkdownOptions = {},
): ReactNode[] {
  const out: ReactNode[] = []
  let seq = 0

  function keyed(node: ReactNode, key: string): ReactNode {
    if (typeof node === 'string' || typeof node === 'number') {
      return <React.Fragment key={key}>{node}</React.Fragment>
    }
    return React.isValidElement(node) ? React.cloneElement(node, { key }) : node
  }

  for (const seg of scanShortcodes(text)) {
    if (typeof seg === 'string') {
      for (const node of renderInlineMarkdown(seg, options)) {
        out.push(keyed(node, `inline-${seq++}`))
      }
    } else {
      out.push(<ShortcodeEmbed shortkey={seg.key} key={`embed-${seq++}`} />)
    }
  }
  return out
}

/** One paragraph-or-image unit of a section body. */
export type SectionBlock =
  | { kind: 'text'; text: string }
  | { kind: 'image'; alt: string; src: string }

/**
 * Split a section's `p` into renderable blocks.
 *
 * Blank lines separate blocks (the ingest path joins docx paragraphs with
 * `\n\n`), and a block that is nothing but an image becomes an `image` block
 * rather than a `text` block — that keeps a figure out of the paragraph flow,
 * so it gets its own line instead of being wedged into a line-height box
 * between two runs of Chinese text.
 *
 * Newlines *within* a block are preserved rather than collapsed. An operator
 * who soft-returns mid-paragraph means it as a line break, and CSS
 * `whitespace-pre-line` on the render side honours it. Collapsing would
 * silently reflow their copy.
 */
export function splitSectionBody(p: string): SectionBlock[] {
  if (!p.trim()) return []
  const blocks: SectionBlock[] = []
  for (const raw of p.split(/\n{2,}/)) {
    const text = raw.trim()
    if (!text) continue

    // Anchored + non-capturing on the tail, so a match here means the image
    // IS the whole block — a line that is prose with an image mid-sentence
    // stays a text block and renders inline.
    const imgOnly = text.match(/^!\[[^\]]*\]\(([^)\s]+)\)$/)
    if (imgOnly) {
      const alt = text.slice(2, text.indexOf(']('))
      blocks.push({ kind: 'image', alt, src: imgOnly[1] ?? '' })
      continue
    }
    blocks.push({ kind: 'text', text })
  }
  return blocks
}